import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import moment from "moment";

import { showError, showSuccess } from "redux/actions/ui";
import { monthNames } from "helpers/Helpers";

import { StatusCode } from "api/protocols";
import {
  GetTeeSheetOpenWindow,
  GetTeeSheets,
  ITeeSheet,
  PutTeeSheetOpenWindow,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";

import { useAppDispatch, useAppSelector } from "hooks/redux";

import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Callout from "components/callout/Callout";
import { Badge } from "components/badge/Badge";
import Page from "components/page/Page";
import { Select } from "components/select/index";
import Sheet from "components/sheet/Sheet";

import "../calendar/teesheetCalendar.scss";
// import Tooltip from "components/tooltip/Tooltip";
import { useHistory } from "react-router";
import Spin from "components/spin/spin";
import TimePick from "components/timePick/TimePick";
import DatePickerInput from "components/datePickerInput/DatePickerInput";

interface ITemplate {
  end_time: string;
  facility_id: number;
  course_id: number;
  format: string;
  holes: number;
  id: number;
  interval: number;
  name: string;
  start_time: string;
}

interface IMainState {
  teesheets: ITeeSheet[];
  selectedTeeSheets: number[];
  applyTemplateActive: boolean;
  templates: ITemplate[];
  selectedTemplate: number;
  checked?: boolean | null;
  selectedCalendarDate: Date;
  courseId: number;
  viewOpenCloseTimes: boolean;
  markHolidays: boolean;
  holidaysAddPopupVisible: boolean;
  holidaysRemovePopupVisible: boolean;
  addTemplatePopupVisible: boolean;
  months: any[];
  years: any[];
  selectedQuickDateMonth: number;
  selectedQuickDateYear: number;
}

interface IOpenWindowState {
  editOpenWindowVisible: boolean;
  openTime: string;
  closeTime: string;
  openWindows: Record<string, any>;
  selectedOpenWindows: Array<number>;
  startDate: Date;
  endDate: Date;
  dateRangeSelected: boolean;
}

export interface IApplyData {
  template_id: number;
  tee_sheet_id: number;
}

enum StateType {
  MainState = "IMainState",
}

const OpenWindows: React.FC = () => {
  const facilityStore = useAppSelector(store => store.facilityStore);
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { Option } = Select;
  const { t } = useTranslation();

  const [state, setState] = useState<IMainState>({
    teesheets: [],
    selectedTeeSheets: [],
    applyTemplateActive: false,
    templates: undefined,
    selectedTemplate: null,
    checked: null,
    selectedCalendarDate: new Date(),
    courseId: undefined,
    viewOpenCloseTimes: true,
    markHolidays: false,
    holidaysAddPopupVisible: false,
    holidaysRemovePopupVisible: false,
    addTemplatePopupVisible: false,
    months: [],
    years: [],
    selectedQuickDateMonth: new Date().getMonth() + 1,
    selectedQuickDateYear: new Date().getFullYear(),
  });

  const [openWindowState, setOpenWindowState] = useState<IOpenWindowState>({
    editOpenWindowVisible: false,
    openTime: "06:00",
    closeTime: "20:00",
    openWindows: {},
    selectedOpenWindows: [],
    startDate: new Date(),
    endDate: new Date(),
    dateRangeSelected: false,
  });
  const [editSheetKey, setEditSheetKey] = useState(0);

  // Filtered TeeSheet[] specific to calendar month and courseId
  const filteredTeeSheets = useMemo(() => {
    return (
      state.courseId !== undefined &&
      !isNaN(state.courseId) &&
      state.teesheets.length !== 0 &&
      state.teesheets.filter(teesheet => {
        const teesheetDate = new Date(teesheet?.date + " 00:00"); //capture date

        //determine related TeeSheets
        return (
          teesheetDate.toString() !== "Invalid Date" &&
          teesheetDate.getFullYear() === state.selectedCalendarDate.getFullYear() &&
          teesheetDate.getMonth() === state.selectedCalendarDate.getMonth() &&
          state.courseId === teesheet?.course_id
        );
      })
    );
  }, [state.selectedCalendarDate, state.courseId, state.teesheets]);

  useEffect(() => {
    void loadTeeSheets();
  }, []);

  useEffect(() => {
    const months: { month: any; id: number }[] = [];
    const years: { year: number; id: number }[] = [];

    monthNames(t).map((monthName: any, index: number) => {
      const month = {
        month: monthName,
        id: index + 1,
      };

      months.push(month);
    });

    let startingYear = new Date().getFullYear() - 5;
    const endingYear = new Date().getFullYear() + 5;

    for (let i = startingYear; i <= endingYear; i++) {
      const year = {
        year: startingYear,
        id: startingYear,
      };

      years.push(year);
      startingYear++;
    }

    setState(prevState => ({ ...prevState, months: months, years: years }));
  }, []);

  useEffect(() => {
    if (state.selectedCalendarDate && state.viewOpenCloseTimes && state.courseId) {
      void getOpenWindows();
    }
  }, [state.selectedCalendarDate, state.viewOpenCloseTimes, state.courseId]);

  useEffect(() => {
    if (state.courseId === undefined || isNaN(state.courseId)) {
      updateState({ courseId: facilityStore.facility?.courses?.[0]?.id }, StateType.MainState);
    }
  }, [facilityStore.facility]);

  async function getOpenWindows() {
    if (!state.courseId) {
      return;
    }

    const windowsRes = await GetTeeSheetOpenWindow(
      { month: state.selectedCalendarDate.getMonth() + 1, course_id: state.courseId },
      true,
    );
    if (windowsRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting hours of operation")));
      return;
    }
    const formatOpenWindows = windowsRes?.data?.reduce(
      (formattedTest: { [key: string]: Record<string, any> }, currentDay: Record<string, any>) => {
        return { ...formattedTest, [currentDay.date]: currentDay };
      },
      {},
    );
    setOpenWindowState(prevState => ({ ...prevState, openWindows: formatOpenWindows }));
  }

  function updateState<T>(newState: Partial<T>, type: StateType) {
    const states = {
      [StateType.MainState]: (newState: Partial<T>) =>
        setState((cur: IMainState) => {
          return { ...cur, ...newState };
        }),
    };

    states[type](newState);
  }

  const onSelectedTeeSheetChange = (newId: number) => {
    if (newId === undefined || isNaN(newId)) {
      return;
    }

    if (state.selectedTeeSheets.some(selectedId => selectedId === newId)) {
      const updatedSelectedTeeSheets = state.selectedTeeSheets.filter(teesheetId => {
        if (teesheetId === newId) {
          return false;
        } else {
          return true;
        }
      });

      updateState<IMainState>({ selectedTeeSheets: updatedSelectedTeeSheets }, StateType.MainState);
    } else {
      //TODO: Remove checks once backend updated to queuing system
      //Only apply checks if 'Mark Holidays' is toggled off
      if (!state.markHolidays) {
        const selectedTeeSheetTemplateTitles = state.teesheets
          .filter(teesheet => state.selectedTeeSheets.includes(teesheet.id))
          .map(value => value.tee_sheet_template_title);

        // limit of 7 selected TeeSheets at a time.
        if (selectedTeeSheetTemplateTitles.length > 6) {
          dispatch(showError(t("Maximum of 7 days."), 2500));
          return;
        }

        // cannot select past dates
        if (
          moment(filteredTeeSheets.find(teeSheet => teeSheet.id === newId).date).isBefore(
            moment(new Date()).format("YYYY-MM-DD").toString(),
            "day",
          )
        ) {
          dispatch(showError(t("Cannot select past dates"), 2500));
          return;
        }

        // Always apply first selected tee sheet
        if (selectedTeeSheetTemplateTitles.length !== 0) {
          // limit to 1 teesheet if template found with the currently selected teesheet
          if (selectedTeeSheetTemplateTitles[0] !== null) {
            dispatch(showError(t("Only one tee sheet can be updated at a time"), 2500));
            return;
          }

          // disable select if currently selecting teesheets are null
          if (
            selectedTeeSheetTemplateTitles[0] === null &&
            filteredTeeSheets.find(teeSheet => teeSheet.id === newId).tee_sheet_template_title !== null
          ) {
            dispatch(showError(t("Can not apply templates at the same time as updating others")));
            return;
          }
        }
      }

      updateState<IMainState>({ selectedTeeSheets: [...state.selectedTeeSheets, newId] }, StateType.MainState);
    }
  };

  const loadTeeSheets = async () => {
    try {
      const res = await GetTeeSheets({}, true);
      // load to state
      updateState<IMainState>({ teesheets: res.data }, StateType.MainState);
    } catch (e) {
      console.log("load tee sheets", e);
    }
  };

  const cancelApplyTemplate = () => {
    updateState<IMainState>(
      { applyTemplateActive: false, selectedTeeSheets: [], checked: false, addTemplatePopupVisible: false },
      StateType.MainState,
    );
  };

  const onTemplateChange = (value: number) => {
    updateState<IMainState>({ selectedTemplate: value }, StateType.MainState);
  };

  function getSelectedOpenWindowDates(openWindows: Record<string, any>, selectedIds: number[]) {
    const selected = new Set(selectedIds);
    return Object.values(openWindows)
      .filter(
        (openWindow: { id?: number; date?: string }) =>
          typeof openWindow?.id === "number" && selected.has(openWindow.id) && typeof openWindow.date === "string",
      )
      .map((openWindow: { date: string }) => openWindow.date)
      .sort();
  }

  function dateFromCalendarString(date: string) {
    return new Date(date.replace(/-/g, "/"));
  }

  function describeSelectedDates(dates: string[]) {
    if (dates.length === 0) {
      return "";
    }

    const labels = dates.map(date => moment(date, "YYYY-MM-DD").format("MMM D"));
    if (dates.length === 1) {
      return labels[0];
    }

    const daySpan =
      moment(dates[dates.length - 1], "YYYY-MM-DD").diff(moment(dates[0], "YYYY-MM-DD"), "days") + 1;
    if (daySpan === dates.length) {
      return `${labels[0]} – ${labels[labels.length - 1]}`;
    }
    if (labels.length <= 8) {
      return labels.join(", ");
    }

    return t("{{count}} selected days, from {{start}} to {{end}}", {
      count: dates.length,
      start: labels[0],
      end: labels[labels.length - 1],
    });
  }

  function selectionIsContinuousRange(dates: string[]) {
    if (dates.length === 0) {
      return false;
    }
    if (dates.length === 1) {
      return true;
    }

    const daySpan = moment(dates[dates.length - 1], "YYYY-MM-DD").diff(moment(dates[0], "YYYY-MM-DD"), "days") + 1;
    return daySpan === dates.length;
  }

  function describeCommittedRange(start: Date, end: Date) {
    const startLabel = moment(start).format("MMM D, YYYY");
    const endLabel = moment(end).format("MMM D, YYYY");
    if (moment(start).isSame(end, "day")) {
      return t("{{date}} will be updated.", { date: startLabel });
    }

    return t("Every day from {{start}} to {{end}} will be updated.", {
      start: startLabel,
      end: endLabel,
    });
  }

  const primaryAction = {
    content: t("Edit Hours of Operation"),
    action: () => {
      setEditSheetKey(key => key + 1);
      setOpenWindowState(prevState => {
        const dates = getSelectedOpenWindowDates(prevState.openWindows, prevState.selectedOpenWindows);
        const startDate = dates.length > 0 ? dateFromCalendarString(dates[0]) : new Date();
        const endDate = dates.length > 0 ? dateFromCalendarString(dates[dates.length - 1]) : new Date();

        return {
          ...prevState,
          editOpenWindowVisible: true,
          startDate,
          endDate,
          dateRangeSelected: selectionIsContinuousRange(dates),
        };
      });
    },
  };

  const onCourseChange = (value: number) => {
    updateState<IMainState>({ courseId: value, selectedTeeSheets: [] }, StateType.MainState);
    setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] }));
  };

  function getDaysInPreviousMonth(date: Date) {
    return new Date(date.getFullYear(), date.getMonth()).getDay();
  }

  function getDaysInMonth(date: Date) {
    return 32 - new Date(date.getFullYear(), date.getMonth(), 32).getDate();
  }

  function getDaysInNextMonth(date: Date) {
    return 7 * 6 - (getDaysInPreviousMonth(date) + getDaysInMonth(date));
  }

  function shiftMonth(monthsToShiftBy: number) {
    const newDate = new Date(state.selectedCalendarDate.getFullYear(), state.selectedCalendarDate.getMonth());
    newDate.setMonth(newDate.getMonth() + monthsToShiftBy);

    updateState<IMainState>(
      {
        selectedCalendarDate: newDate,
        selectedQuickDateMonth: newDate.getMonth() + 1,
        selectedQuickDateYear: newDate.getFullYear(),
      },
      StateType.MainState,
    );
    setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] }));
  }

  function resetEditOpenWindowForm(clearSelection: boolean) {
    setOpenWindowState(prevState => ({
      ...prevState,
      editOpenWindowVisible: false,
      openTime: "06:00",
      closeTime: "20:00",
      startDate: new Date(),
      endDate: new Date(),
      dateRangeSelected: false,
      selectedOpenWindows: clearSelection ? [] : prevState.selectedOpenWindows,
    }));
  }

  async function openWindowIdsForSelectedRange() {
    const start = moment(openWindowState.startDate);
    const end = moment(openWindowState.endDate);
    const rangeStart = (start.isAfter(end, "day") ? end : start).format("YYYY-MM-DD");
    const rangeEnd = (start.isAfter(end, "day") ? start : end).format("YYYY-MM-DD");

    const windowsRes = await GetTeeSheetOpenWindow(
      {
        course_id: state.courseId,
        start_date: rangeStart,
        end_date: rangeEnd,
      },
      true,
    );

    if (windowsRes.status !== StatusCode.OK) {
      return null;
    }

    const windows = Array.isArray(windowsRes.data) ? windowsRes.data : [];
    return windows
      .map((openWindow: { id?: number }) => openWindow?.id)
      .filter((id): id is number => typeof id === "number");
  }

  async function handleApplyOpenWindow() {
    let openWindowIds = [...openWindowState.selectedOpenWindows];

    if (openWindowState.dateRangeSelected) {
      const rangeIds = await openWindowIdsForSelectedRange();
      if (rangeIds == null) {
        dispatch(showError(t("Error getting hours of operation")));
        return;
      }
      if (rangeIds.length === 0) {
        dispatch(showError(t("No hours of operation found for that date range")));
        return;
      }
      openWindowIds = rangeIds;
    }

    if (openWindowIds.length === 0) {
      dispatch(showError(t("Select the dates on the calendar or pick a date range and try again")));
      return;
    }

    const openWindowRes = await PutTeeSheetOpenWindow(
      {
        open_time: openWindowState.openTime ?? null,
        close_time: openWindowState.closeTime ?? null,
        open_window_ids: openWindowIds,
      },
      true,
    );
    if (openWindowRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating hours of operation")));
      return;
    }
    dispatch(showSuccess(t("Successfully updated hours of operation")));
    resetEditOpenWindowForm(true);
    await getOpenWindows();
  }

  function onSelectedOpenWindowChange(id: number) {
    if (id == null) {
      return;
    }
    const updatedOpenWindowSelection = [...openWindowState.selectedOpenWindows];
    const foundIndex = updatedOpenWindowSelection.indexOf(id);
    if (foundIndex === -1) {
      updatedOpenWindowSelection.push(id);
    } else {
      updatedOpenWindowSelection.splice(foundIndex, 1);
    }
    setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: updatedOpenWindowSelection }));
  }

  function selectAllOpenWindows() {
    const openWindows = { ...openWindowState.openWindows };
    const allOpenWindowIds = Object.keys(openWindows).map(keyName => {
      return openWindows[keyName]?.id as number;
    });
    setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: allOpenWindowIds }));
  }

  const handleMonthChange = (value: number) => {
    const newDate = new Date(state.selectedQuickDateYear, value - 1);
    newDate.setMonth(newDate.getMonth());
    ReactDOM.unstable_batchedUpdates(() => {
      updateState<IMainState>({ selectedQuickDateMonth: value, selectedCalendarDate: newDate }, StateType.MainState);
      setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] }));
    });
  };

  const handleYearChange = (value: number) => {
    const newDate = new Date(value, state.selectedQuickDateMonth - 1);
    newDate.setMonth(newDate.getMonth());
    ReactDOM.unstable_batchedUpdates(() => {
      updateState<IMainState>({ selectedQuickDateYear: value, selectedCalendarDate: newDate }, StateType.MainState);
      setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] }));
    });
  };

  const selectedDates = getSelectedOpenWindowDates(
    openWindowState.openWindows,
    openWindowState.selectedOpenWindows,
  );

  const handleDateNavigation = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, dateString: string) => {
    e.stopPropagation();

    const passingDate = new Date(dateString.replace(/-/g, "/"));
    history.push("/admin/teesheet", { params: { teeSheetDate: passingDate } });
  };

  return (
    <Page
      title={t("Hours of Operation")}
      subtitle={t("View hours of operation applied throughout the season")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Select All"),
              handler: selectAllOpenWindows,
              disableAutoClose: true,
            },
            {
              type: "handler",
              label: t("Unselect All"),
              handler: () => setOpenWindowState(prevState => ({ ...prevState, selectedOpenWindows: [] })),
              disableAutoClose: true,
            },
          ],
        },
      }}
    >
      {state.teesheets?.length > 0 &&
        state.courseId &&
        !isNaN(state.courseId) &&
        (() => {
          const courses = facilityStore?.facility?.courses ?? [];

          return (
            <>
              {courses.length > 1 ? (
                <div className="courses-dropdown-container">
                  <Select className="courses-dropdown" defaultValue={state.courseId} onChange={onCourseChange}>
                    {courses.map((course: any) => {
                      return (
                        <Option value={course?.id} key={course?.id}>
                          {course?.full_name}
                        </Option>
                      );
                    })}
                  </Select>
                </div>
              ) : null}

              <div className="calendar-quick-date-selection">
                <button className="navigation-button" onClick={() => shiftMonth(-1)}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                <div className="calendar-quick-date-picker">
                  <div>
                    <Select defaultValue={state.selectedQuickDateMonth} onChange={handleMonthChange}>
                      {state.months?.map(month => {
                        return (
                          <Option value={month.id} key={month.id}>
                            {month.month}
                          </Option>
                        );
                      })}
                    </Select>
                  </div>

                  <div>
                    <Select defaultValue={state.selectedQuickDateYear} onChange={handleYearChange}>
                      {state.years?.map(year => {
                        return (
                          <Option value={year.id} key={year.id}>
                            {year.year}
                          </Option>
                        );
                      })}
                    </Select>
                  </div>
                </div>

                <button className="navigation-button" onClick={() => shiftMonth(1)}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>

              {filteredTeeSheets && filteredTeeSheets.length > 0 ? (
                <>
                  <div className="grid-container">
                    {moment?.weekdays()?.map((day, i) => {
                      return (
                        <div key={i} className="day-title">
                          <p>{day?.substring(0, 3)}</p>
                        </div>
                      );
                    })}
                    {Array.from(Array(getDaysInPreviousMonth(state.selectedCalendarDate)).keys()).map(i => {
                      return <div key={i} className="grid-item"></div>;
                    })}
                    {filteredTeeSheets.map((teesheet, i) => {
                      return (
                        <div key={i}>
                          <div
                            className={`grid-item teesheet ${state.selectedTeeSheets.some(selectedId => selectedId === teesheet?.id) ||
                              openWindowState.selectedOpenWindows.some(
                                selectedId => selectedId === openWindowState.openWindows[teesheet.date]?.id,
                              )
                              ? "selected-teesheet"
                              : "unselected-teesheet"
                              }`}
                            onClick={
                              state.viewOpenCloseTimes
                                ? () => onSelectedOpenWindowChange(openWindowState.openWindows[teesheet.date]?.id as number)
                                : () => onSelectedTeeSheetChange(teesheet?.id)
                            }
                            key={i}
                          >
                            {!!teesheet?.holiday && (
                              <div className="edit-icon">
                                <Badge type="primary" size="small">
                                  {t("Holiday")}
                                </Badge>
                              </div>
                            )}
                            <div className="day-of-month" onClick={e => handleDateNavigation(e, teesheet.date)}>
                              <p>{i + 1}</p>
                            </div>
                            <div className="grid-content">
                              {state.viewOpenCloseTimes ? (
                                <>
                                  <div className="open-window-container">
                                    <span>{t("Open:")} </span>
                                    <span>
                                      {openWindowState.openWindows[teesheet.date]?.open_time
                                        ? moment(
                                          openWindowState.openWindows[teesheet.date]?.open_time as string,
                                          "hh:mm:ss",
                                        ).format("h:mm A")
                                        : ""}
                                    </span>
                                  </div>
                                  <div className="open-window-container">
                                    <span>{t("Close:")} </span>
                                    <span>
                                      {openWindowState.openWindows[teesheet.date]?.close_time
                                        ? moment(
                                          openWindowState.openWindows[teesheet.date]?.close_time as string,
                                          "hh:mm:ss",
                                        ).format("h:mm A")
                                        : ""}
                                    </span>
                                  </div>
                                </>
                              ) : teesheet.tee_sheet_template_title ? (
                                <div className="template-title-wrap">
                                  <span className="template-title">{teesheet.tee_sheet_template_title}</span>
                                  {teesheet.tee_sheet_template_version && (
                                    <span className="calendar-version">
                                      Version {teesheet.tee_sheet_template_version}
                                    </span>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {Array.from(Array(getDaysInNextMonth(state.selectedCalendarDate)).keys()).map(i => {
                      return <div key={i} className="grid-item"></div>;
                    })}
                  </div>
                </>
              ) : (
                <Callout
                  type="error"
                  title={t("There are no Tee Sheets for this month and course")}
                  content={t("To create tee Sheets go to Template Calendar and click Options > Create Tee Sheets")}
                />
              )}

              <Sheet
                open={state.applyTemplateActive}
                closable
                title={t("Select Template")}
                onCancel={cancelApplyTemplate}
                onOk={() => setState(prevState => ({ ...prevState, addTemplatePopupVisible: true }))}
                cancelText={t("Clear")}
                okText={t("Apply")}
                size="small"
                overflow
                okDisabled={state.selectedTemplate === null || state.selectedTeeSheets.length === 0}
              >
                <Select
                  defaultValue={state.templates?.find(template => template?.course_id === state.courseId)?.id}
                  onChange={onTemplateChange}
                >
                  {state.templates ? (
                    <>
                      {state.templates
                        .filter(template => template?.course_id === state.courseId)
                        .map(template => {
                          return (
                            <Option value={template.id} key={template.id}>
                              {template.name}
                            </Option>
                          );
                        })}
                    </>
                  ) : (
                    <Option value={"spinner"}>
                      <div style={{ height: "40px" }}>
                        <Spin />
                      </div>
                    </Option>
                  )}
                </Select>
              </Sheet>

              <Sheet
                open={openWindowState.editOpenWindowVisible}
                closable
                overflow
                title={t("Edit Hours of Operation")}
                onCancel={() => resetEditOpenWindowForm(false)}
                onOk={handleApplyOpenWindow}
                cancelText={t("Close")}
                okText={t("Save")}
                size="small"
                okDisabled={
                  !openWindowState.openTime ||
                  !openWindowState.closeTime ||
                  (openWindowState.selectedOpenWindows.length === 0 && !openWindowState.dateRangeSelected)
                }
              >
                <Form>
                  <FormLayout>
                    {selectedDates.length > 0 && !openWindowState.dateRangeSelected ? (
                      <FormLayout.Group>
                        <Callout
                          type="info"
                          title={t("Selected days")}
                          content={describeSelectedDates(selectedDates)}
                        />
                      </FormLayout.Group>
                    ) : null}
                    {openWindowState.dateRangeSelected ? (
                      <FormLayout.Group>
                        <Callout
                          type="info"
                          title={t("Date range")}
                          content={describeCommittedRange(openWindowState.startDate, openWindowState.endDate)}
                        />
                      </FormLayout.Group>
                    ) : null}
                    <FormLayout.Group>
                      <DatePickerInput
                        key={editSheetKey}
                        label={t("Date Range")}
                        months={1}
                        position="left"
                        usePortal
                        showQuickOptions
                        startingDate={openWindowState.startDate}
                        setStartingDate={(date: Date) =>
                          setOpenWindowState(prev => ({ ...prev, startDate: date, dateRangeSelected: true }))
                        }
                        endingDate={openWindowState.endDate}
                        setEndingDate={(date: Date) =>
                          setOpenWindowState(prev => ({ ...prev, endDate: date, dateRangeSelected: true }))
                        }
                        helpText={
                          selectedDates.length > 0 && !openWindowState.dateRangeSelected
                            ? t(
                                "Selected days are shown above. Apply a range here to update every day from the start date through the end date.",
                              )
                            : selectedDates.length > 0
                              ? undefined
                              : t("Apply a range to update every day between these dates.")
                        }
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <TimePick
                        value={openWindowState.openTime}
                        onChange={timeString => setOpenWindowState(prev => ({ ...prev, openTime: timeString }))}
                        label={t("Open Time")}
                        size="large"
                        status={openWindowState.openTime === undefined ? "warning" : undefined}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <TimePick
                        value={openWindowState.closeTime}
                        onChange={timeString => setOpenWindowState(prev => ({ ...prev, closeTime: timeString }))}
                        label={t("Close Time")}
                        size="large"
                        status={openWindowState.closeTime === undefined ? "warning" : undefined}
                      />
                    </FormLayout.Group>
                  </FormLayout>
                </Form>
                {openWindowState.selectedOpenWindows.length === 0 && !openWindowState.dateRangeSelected ? (
                  <Callout
                    type="error"
                    title={t("No Dates Selected")}
                    content={t("Select dates on the calendar or pick a date range, then try again")}
                  />
                ) : null}
              </Sheet>
            </>
          );
        })()}
    </Page>
  );
};

export default OpenWindows;
